import get from 'lodash/get';
import { convertDatetimeToFixedInterval, getTimeInSeconds } from 'utils/time';
import {
  siteManageVisitorKey,
  siteRequiresPassKey,
  siteRequiresPhotoIdKey,
  siteRequiresPhotoKey,
} from 'utils/constants/visitor';
import {
  staticMapCustomMarker,
  defaultStaticMapMarkerProps,
  defaultStaticMapPathProps,
} from 'utils/constants/Map';
import { transformPatrolTaskEvidence } from '../../Task/taskForm';
import { getItemOrDefaultToValue } from 'utils/details';
import { Buffer } from 'buffer';
const transformLocation = (locations, data) => {
  let numOfCheckPoints = 0;

  const locationEvidence = [];

  const newLocationsArray = locations.map(location => {
    if (location.text > numOfCheckPoints) {
      numOfCheckPoints = location.text;
    }

    const taskEvidence = transformPatrolTaskEvidence({
      evidence: data.locationEvidence,
      taskName: data.patrolDefinitionName,
      location,
      businessClient: data.businessClient,
      site: data.businessClient,
    });

    taskEvidence && locationEvidence.push(taskEvidence);

    return {
      checkPointSequence: location.text,
      point: {
        latitude: location.lat,
        longitude: location.lng,
        isWaypoint: location.type === 'WAYPOINT',
      },
      taskId: taskEvidence ? taskEvidence.id : null,
      qrCode: get(location, 'qrCode'),
    };
  });
  if (
    (data.returnToStart &&
      newLocationsArray[0].point.latitude !==
        newLocationsArray[newLocationsArray.length - 1].point.latitude &&
      newLocationsArray[0].point.longitude !==
        newLocationsArray[newLocationsArray.length - 1].point.longitude) ||
    newLocationsArray.length === 1
  ) {
    return {
      locationEvidence,
      locations: [...newLocationsArray, { ...newLocationsArray[0] }],
    };
  } else {
    return { locationEvidence, locations: newLocationsArray };
  }
};

export const getMarkerData = locations => {
  let counter = 0;
  const checkpoints = locations.filter(
    location => location.type === 'CHECKPOINT',
  );
  //show marker only for checkpoints
  const markerData = checkpoints.reduce((allMarker, location) => {
    let marker;
    counter += 1;
    if (counter === 1) {
      marker = `markers=icon:${staticMapCustomMarker}|${location.lat},${location.lng}&`;
    } else {
      marker = `markers=${defaultStaticMapMarkerProps}${location.lat},${location.lng}&`;
    }
    return allMarker.concat(marker);
  }, '');
  //show path for both checkpoints and waypoints
  const pathData = locations.reduce((allPath, location) => {
    const path = `|${location.lat},${location.lng}`;
    return allPath.concat(path);
  }, '');
  const markerWithPath = `${markerData}path=${defaultStaticMapPathProps}${pathData}|${locations[0].lat},${locations[0].lng}`;
  return markerWithPath;
};

export const convertImageToBase64 = (image, currentFormat) => {
  const tranformedImage = Buffer.from(image, currentFormat).toString('base64');
  const image64 = `data:image/jpeg;base64,${tranformedImage}`;
  return image64;
};

export const transformPatrolReportObject = (patrolDef, regionId, timezone) => {
  return {
    id: patrolDef.id,
    definitionName: get(patrolDef, 'patrolName'),
    securityCompanyId: get(patrolDef, 'securityCompanyId'),
    securityCompanyName: get(patrolDef, 'securityCompanyName'),
    regionId,
    isMultiSite: get(patrolDef, 'isMultiSite'),
    businessClient: {
      id: get(patrolDef, 'businessClient.id'),
      businessClientId: get(patrolDef, 'businessClient.businessClientId'),
      name: get(patrolDef, 'businessClient.name'),
    },
    businessClientName: patrolDef.businessClientName,
    site: get(patrolDef, 'isMultiSite', false)
      ? undefined
      : {
          id: get(patrolDef, 'site.id'),
          siteId: get(patrolDef, 'site.siteId'),
          siteName: get(patrolDef, 'site.siteName'),
          inductionRequired: get(patrolDef, 'site.inductionRequired', false),
        },
    siteId: patrolDef.siteId,
    siteName: patrolDef.siteName,
    timezone,
  };
};

export const transformPatrolDefObject = data => {
  const ids = data?.departments &&
    data?.departments.length > 0 && {
      departmentIds: getItemOrDefaultToValue(
        data,
        'departments',
        undefined,
      )?.map(m => m.id),
    };

  const { locationEvidence, locations } = transformLocation(
    get(data, 'checkpoints'),
    data,
  );

  if (!data.returnToStart) {
    if (
      locations.length > 1 &&
      // if first and last latitude and longitude matches
      locations[0].point.latitude ===
        locations[locations.length - 1].point.latitude &&
      locations[0].point.longitude ===
        locations[locations.length - 1].point.longitude
    ) {
      // remove last location
      locations.pop();
    }
  }
  return {
    definitionName: get(data, 'patrolDefinitionName'),
    archived: get(data, 'archived'),
    timezone: get(data, 'timezone'),
    firstLocation: locations[0],
    locations: locations.slice(1), //remove first point as it is copied to firstLocation key
    expectedDuration: getTimeInSeconds(get(data, 'expectedDuration')),
    ...(ids && { departmentIds: ids.departmentIds }),
    schedule: get(data, 'schedule')
      ? {
          days: get(data, 'schedule'),
          startTime:
            data.startTime &&
            convertDatetimeToFixedInterval(get(data, 'startTime')), // transform
          endTime:
            data.endTime &&
            convertDatetimeToFixedInterval(get(data, 'endTime')), // transform
          interval: get(data, 'schedule') ? get(data, 'interval') : undefined,
        }
      : undefined,
    isMultiSite: get(data, 'mobilePatrol'),
    requiredServices: get(data, 'requiredServices'),
    businessClient: {
      id: get(data, 'site.businessClientId'),
      businessClientName: get(data, 'site.businessClientName'),
      businessClientId: get(data, 'site.businessClientReadableId'),
    },
    securityCompanyId: get(data, 'securityCompanyId'),
    securityCompanyName: get(data, 'securityCompanyName'),
    regionId: get(data, 'regionId'),
    site: get(data, 'mobilePatrol', false)
      ? undefined
      : {
          id: get(data, 'site.id'),
          siteId: get(data, 'site.siteId'),
          siteName: get(data, 'site.siteName'),
          inductionRequired: get(data, 'site.inductionRequired', false),
        },

    ...(get(data, 'siteSurfaceImages') && {
      surfaces: [get(data, 'siteSurfaceImages')],
    }),
    ...(get(data, 'siteSurfaceImages') && {
      surfaceId: get(data, 'siteSurfaceImages.id'),
    }),

    patrolImgData: {
      markers: getMarkerData(get(data, 'checkpoints')),
      size: '400x400',
      mapType: 'roadmap',
    },
    newSite: get(data, 'newSite'),
    newBusinessClient: get(data, 'newBusinessClient'),
    newSiteContact: get(data, 'newSiteContact'),
    ...(data.siteData && {
      siteData: {
        ...data.siteData,
        [siteManageVisitorKey]: data[siteManageVisitorKey],
        [siteRequiresPassKey]: data[siteRequiresPassKey],
        [siteRequiresPhotoIdKey]: data[siteRequiresPhotoIdKey],
        [siteRequiresPhotoKey]: data[siteRequiresPhotoKey],
        address: data.address,
        locations: data.siteLocations,
        siteId: data.siteId,
        name: data.name,
        timezone: data.timezone,
        newSite: get(data, 'newSite'),
        newBusinessClient: get(data, 'newBusinessClient'),
        newSiteContact: get(data, 'newSiteContact'),
      },
    }),
    ...(data.locationEvidence && {
      locationEvidence,
    }),
    proximityAlertDisabled: data.proximityAlertDisabled,
  };
};

export const transformPatrolWithNewSite = (patrol, newSite) => {
  return transformPatrolDefObject({
    ...patrol,
    site: {
      businessClientId: newSite.data.businessClient.id,
      businessClientName: newSite.data.businessClient.businessClientName,
      businessClientReadableId: newSite.data.businessClient.businessClientId,
      id: newSite.data.id,
      siteId: newSite.data.siteId,
      siteName: newSite.data.name,
    },
  });
};
